import {
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputBase,
  Autocomplete,
  TextField,
  styled,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Email as MailIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  PeopleAlt as PeopleIcon,
  WhereToVote as ActiveIcon,
  EventBusy as ExpiringIcon,
  DoNotDisturbOn as InactiveIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";

import MessageDialog from "../componenets/client/MessageDialog";
import SortableTableCell from "../componenets/SortableTableCell";
import {ConfirmDialog, StatusChip, FilterDropdown, ActionMenu, Pagination} from "../componenets/ui";
import {deleteTrainees, disableClients, enableClients, getLiveClients, getEnhancedStats} from "../services/TraineeService";
import {getTrainers} from "../services/TrainerService";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {useCompany} from "../contexts/CompanyContext";
import {useAuth} from "../contexts/AuthContext";
import {RTL} from "../cache/CacheRtl";
import {ReactComponent as CancelIcon} from "../assets/images/cancel.svg";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";
import {colors, borders, shadows} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";
import DashboardTable from "../componenets/dashboard/coach/DashboardTable";

/* ── Styled trainer autocomplete ─────────────────────────── */
const TrainerSelect = styled(Autocomplete)({
  minWidth: "160px",
  "& .MuiOutlinedInput-root": {
    color: colors.textSecondary, borderRadius: borders.radius.md,
    backgroundColor: colors.white, fontSize: "14px", fontWeight: 500, height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {border: borders.default},
    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary},
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: "2px"},
  },
  "& .MuiInputLabel-root": {color: colors.textSecondary, fontSize: "14px", "&.Mui-focused": {color: colors.primary}},
});

/* ── Helpers ──────────────────────────────────────────────── */
const fmt = (ts) => ts ? moment.unix(ts).format("DD/MM/YY") : "—";
const expired = (ts) => ts && moment.unix(ts).isBefore(moment());
const expiringSoon = (ts) => ts && moment.unix(ts).isBefore(moment().add(30, "days")) && moment.unix(ts).isAfter(moment());

const relativeTime = (epochSeconds) => {
  if (!epochSeconds) return {text: "אף פעם", color: colors.textMuted};
  const days = Math.floor((Date.now() / 1000 - epochSeconds) / 86400);
  if (days === 0) return {text: "היום", color: colors.success};
  if (days === 1) return {text: "אתמול", color: colors.success};
  if (days <= 3) return {text: `לפני ${days} ימים`, color: colors.success};
  if (days <= 7) return {text: `לפני ${days} ימים`, color: colors.textSecondary};
  if (days <= 14) return {text: `לפני ${days} ימים`, color: colors.warningText};
  if (days <= 30) return {text: `לפני ${days} ימים`, color: "#F97316"};
  return {text: `לפני ${days}+ ימים`, color: colors.errorText};
};

const statusColor = (v) => ({
  ACTIVE: colors.success, TRAIL: colors.info, CLOSE_TO_END: colors.warning,
  MISSING: colors.error, IN_ACTIVE: colors.neutral,
  DAYS_SINCE_0_3: colors.success, DAYS_SINCE_4_7: "#34D399",
  DAYS_SINCE_8_14: colors.warning, DAYS_SINCE_15_30: "#F97316",
  DAYS_SINCE_30_PLUS: colors.error,
}[v] || colors.textSecondary);

const STATUS_OPTS = [
  {value: "ALL", label: "הכול"},
  {value: "ACTIVE", label: "פעיל", color: statusColor("ACTIVE")},
  {value: "TRAIL", label: "תקופת ניסיון", color: statusColor("TRAIL")},
  {value: "CLOSE_TO_END", label: "עומד להסתיים", color: statusColor("CLOSE_TO_END")},
  {value: "MISSING", label: "נעדרים", color: statusColor("MISSING")},
  {value: "IN_ACTIVE", label: "לא פעיל", color: statusColor("IN_ACTIVE")},
  {divider: true, label: "לפי ימים מאז אימון"},
  {value: "DAYS_SINCE_0_3", label: "0-3 ימים", color: statusColor("DAYS_SINCE_0_3")},
  {value: "DAYS_SINCE_4_7", label: "4-7 ימים", color: statusColor("DAYS_SINCE_4_7")},
  {value: "DAYS_SINCE_8_14", label: "8-14 ימים", color: statusColor("DAYS_SINCE_8_14")},
  {value: "DAYS_SINCE_15_30", label: "15-30 ימים", color: statusColor("DAYS_SINCE_15_30")},
  {value: "DAYS_SINCE_30_PLUS", label: "30+ ימים", color: statusColor("DAYS_SINCE_30_PLUS")},
];

const GENDER_OPTS = [
  {value: "ALL", label: "הכול"},
  {value: "MALE", label: "זכר"},
  {value: "FEMALE", label: "נקבה"},
];

/* ── Inline helper components ────────────────────────────── */

const ClientAvatar = ({name, email}) => {
  return (
      <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
        <Box sx={{overflow: "hidden", minWidth: 0}}>
          <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
            {name}
          </Typography>
          <Typography sx={{fontSize: "11px", color: colors.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
            {email || "—"}
          </Typography>
        </Box>
      </Box>
  );
};

const WEEK_TYPE_LABELS = {
  RM_WEEK: "RM", FIRST_WEEK: "ראשון", LOAD_WEEK: "Load",
  LOAD_PLUS: "Load+", LOAD_PLUS_PLUS: "Load++", LOAD_PLUS_PLUS_PLUS: "Load+++",
  PRE_PEAK_WEEK: "Pre-Peak", PEAK_WEEK: "Peak", DELOAD_WEEK: "Deload",
};

const programSummary = (c) => {
  const parts = [];
  if (c.currentBlockIndex != null && c.totalBlocks > 0) parts.push(`בלוק ${c.currentBlockIndex + 1}/${c.totalBlocks}`);
  const type = c.weekType ? WEEK_TYPE_LABELS[c.weekType] || c.weekType : "שבוע";
  if (c.currentWeekIndex != null && c.totalWeeksInBlock > 0) parts.push(`${type} ${c.currentWeekIndex + 1}/${c.totalWeeksInBlock}`);
  if (c.workoutsTotalThisWeek > 0) parts.push(`${c.workoutsCompletedThisWeek}/${c.workoutsTotalThisWeek} אימונים`);
  return parts.join(" · ");
};

const WeightTrend = ({trend, status}) => {
  if (trend == null) return <Typography sx={{fontSize: "12px", color: colors.textMuted}}>—</Typography>;
  const clr = status === "GOOD" ? colors.success : status === "BAD" ? colors.error : colors.textMuted;
  const Icon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : TrendingFlat;
  const sign = trend > 0 ? "+" : "";
  return (
      <Box sx={{display: "flex", alignItems: "center", gap: 0.5, color: clr, justifyContent: "center"}}>
        <Icon sx={{fontSize: 16}}/>
        <Typography sx={{fontSize: "12px", fontWeight: 600, color: clr}}>{sign}{trend.toFixed(1)}</Typography>
      </Box>
  );
};

/* ── Table cell presets (match CoachDashboard ClientsTab) ─── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Clients page
   ═══════════════════════════════════════════════════════════ */
const Clients = () => {
  const [messageOpen, setMessageOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [stats, setStats] = useState(null);
  const [trainers, setTrainers] = useState([]);

  const {company} = useCompany();
  const {notificationHandler} = useMessageContext();
  const {isAdmin, isCompanyGroupAdmin} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [f, setF] = useState({
    clients: [], trainer: query.get("trainer") ? +query.get("trainer") : 0,
    total: 0, keyword: query.get("keyword") || "",
    gender: query.get("gender") || "ALL",
    page: +(query.get("page") || 0), size: +(query.get("size") || 15),
    loading: true, state: query.get("state") || "ACTIVE",
    sortBy: query.get("sortBy") || "name",
    sortDirection: query.get("sortDirection") || "ASC",
    reload: 0,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, []);

  // Load stats + trainers once
  useEffect(() => { getEnhancedStats(company?.id).then(r => setStats(r.data)).catch(() => {}); }, [company]);
  useEffect(() => { if (isAdmin()) getTrainers().then(r => setTrainers(r.data)).catch(() => {}); }, []);

  // ?new=1 used to open a dialog here; creation is now the /app/clients/new route. The param is
  // kept working as a redirect so existing bookmarks and automation links still land on the wizard.
  // A company group admin has no creation capabilities, so for them the param is just stripped.
  useEffect(() => {
    if (query.get("new") === "1") {
      if (!isCompanyGroupAdmin()) {
        navigate("/app/clients/new", {replace: true});
        return;
      }
      const p = new URLSearchParams(location.search);
      p.delete("new");
      navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
    }
  }, [location.search]);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.trainer) p.set("trainer", ns.trainer);
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.gender !== "ALL") p.set("gender", ns.gender);
    if (ns.state !== "ALL") p.set("state", ns.state);
    if (ns.page > 0) p.set("page", ns.page);
    if (ns.size !== 15) p.set("size", ns.size);
    if (ns.sortBy && ns.sortBy !== "name") p.set("sortBy", ns.sortBy);
    if (ns.sortDirection && ns.sortDirection !== "ASC") p.set("sortDirection", ns.sortDirection);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: false});
  };

  // Fetch
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getLiveClients(f.page, f.size, f.keyword, f.trainer, f.gender, f.state, company, f.sortBy, f.sortDirection)
        .then(r => setF(p => ({...p, clients: r.data.clients, total: r.data.totalElements, loading: false})))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.size, f.keyword, f.trainer, f.gender, f.state, company, f.sortBy, f.sortDirection, f.reload]);

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };
  const handleSort = (field, direction) => set({sortBy: field, sortDirection: direction});
  // Refetch the current view in place (keeps filters/page/sort) — bumping `reload` re-runs the fetch effect.
  const refetch = () => setF(p => ({...p, reload: p.reload + 1}));
  const toggle = (c) => setSelected(p => p.some(x => x.traineeId === c.traineeId) ? p.filter(x => x.traineeId !== c.traineeId) : [...p, c]);
  const allChecked = f.clients.length > 0 && f.clients.every(c => selected.some(s => s.traineeId === c.traineeId));
  const toggleAll = () => {
    if (allChecked) setSelected([]);
    else setSelected(prev => {
      const ids = new Set(prev.map(s => s.traineeId));
      return [...prev, ...f.clients.filter(c => !ids.has(c.traineeId))];
    });
  };

  // Bulk ops
  const getIds = () => selected.map(c => c.traineeId);
  const afterBulk = (msg) => { setSelected([]); refetch(); notificationHandler.success(msg); };
  const onEnable = () => enableClients(getIds()).then(() => afterBulk("משתמשים הופעלו בהצלחה")).catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  const onDisable = () => disableClients(getIds()).then(() => afterBulk("משתמשים בוטלו בהצלחה")).catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  const onDelete = () => deleteTrainees(getIds()).then(r => { setSelected([]); setDeleteOpen(false); refetch(); r.data.failureCount > 0 ? notificationHandler.warning(`${r.data.successCount} מתוך ${selected.length} נמחקו`) : notificationHandler.success("משתמשים נמחקו בהצלחה"); }).catch(e => { setDeleteOpen(false); notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)); });

  const actions = [
    {label: "הפוך פעיל", icon: <CheckIcon sx={{fontSize: 18}}/>, onClick: onEnable},
    {label: "הפוך ללא פעיל", icon: <BlockIcon sx={{fontSize: 18}}/>, onClick: onDisable},
    {label: "שלח הודעה", icon: <MailIcon sx={{fontSize: 18}}/>, onClick: () => setMessageOpen(true)},
    {label: "מחק", icon: <DeleteIcon sx={{fontSize: 18}}/>, onClick: () => setDeleteOpen(true), danger: true},
  ];

  const pages = Math.ceil(f.total / f.size);

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Dialogs */}
        <MessageDialog setSelectedClients={setSelected} selectedClients={selected} isOpen={messageOpen} close={() => setMessageOpen(false)}/>
        <ConfirmDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={onDelete} title="האם אתה בטוח?" description="פעולה זו לא ניתנת לביטול" variant="danger" confirmText="מחק" icon={<CancelIcon/>}/>

        {/* ─── Title row (no white band — sits on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              לקוחות
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול לקוחות ומעקב</Typography>
          </Box>
          {!isCompanyGroupAdmin() && (
          <Box component="button" onClick={() => navigate("/app/clients/new")} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            לקוח חדש
          </Box>
          )}
        </Box>

        {/* ─── KPI grid ────────────────────────────────── */}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, 1fr)"}, gap: "14px", flexShrink: 0}}>
          <KPI
              icon={<PeopleIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.total?.toLocaleString() ?? "—"} valueColor={t.green}
              label="סה״כ לקוחות"
              sub={stats?.activeThisWeek != null ? `${stats.activeThisWeek} פעילים השבוע` : null}
              onClick={() => set({state: "ALL"})}
          />
          <KPI
              icon={<ActiveIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              badge="התאמנו השבוע" badgeBg={t.greenLight} badgeColor={t.greenText}
              value={stats?.activeThisWeek?.toLocaleString() ?? "—"} valueColor={t.green}
              label="פעילים השבוע"
              onClick={() => set({state: "ACTIVE_THIS_WEEK"})}
          />
          <KPI
              icon={<ExpiringIcon sx={{fontSize: 22}}/>} iconBg={t.amberLight} iconColor={t.amber}
              badge="30 ימים" badgeBg={t.amberLight} badgeColor={t.amberText}
              value={stats?.expiringSoon?.toLocaleString() ?? "—"} valueColor={t.amber}
              label="קרובים לסיום"
              onClick={() => set({state: "CLOSE_TO_END"})}
          />
          <KPI
              icon={<InactiveIcon sx={{fontSize: 22}}/>} iconBg={t.redLight} iconColor={t.red}
              badge="30+ ימים" badgeBg={t.redLight} badgeColor={t.redText}
              value={stats?.inactive?.toLocaleString() ?? "—"} valueColor={t.red}
              label="לא פעילים"
              onClick={() => set({state: "DAYS_SINCE_30_PLUS"})}
          />
        </Box>

        {/* ─── Table card (fixed-height like CoachDashboard's DashboardTable) ─── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card (DashboardTable style) */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0,
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <PeopleIcon sx={{fontSize: 14}}/>
              {f.total} לקוחות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם, אימייל..."
                  sx={{
                    width: 200,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            {/* State + Gender + Trainer — sit next to the search */}
            <FilterDropdown label="מצב" value={f.state} onChange={v => set({state: v})} options={STATUS_OPTS} minWidth={120}/>
            <FilterDropdown label="מגדר" value={f.gender} onChange={v => set({gender: v})} options={GENDER_OPTS} minWidth={90}/>
            {isAdmin() && trainers.length > 0 && (
                <RTL>
                  <TrainerSelect size="small" options={[{id: 0, name: "הכול"}, ...trainers.sort((a, b) => a.name.localeCompare(b.name, "he"))]}
                                 getOptionLabel={o => o.name || ""} value={trainers.find(tr => tr.id === f.trainer) || {id: 0, name: "הכול"}}
                                 onChange={(_, v) => set({trainer: v?.id || 0})} isOptionEqualToValue={(o, v) => o.id === v.id} disableClearable
                                 renderInput={p => <TextField {...p} label="מאמן" size="small"/>}
                                 slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
                  />
                </RTL>
            )}
            <Box sx={{flex: 1}}/>
            <ActionMenu label="פעולות" actions={actions} disabled={!selected.length} count={selected.length}/>
          </Box>

          {/* Table area — fixed-height with internal scroll (matches CoachDashboard) */}
          {f.loading && !f.clients.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.clients.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <Typography sx={{color: t.t3, fontSize: "13px", fontWeight: 500}}>אין לקוחות להצגה</Typography>
                <Typography sx={{color: t.t3, fontSize: "12px"}}>נסה לשנות את הסינון או החיפוש</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, width: 44, padding: "10px 8px"}}>
                          <Checkbox size="small" checked={allChecked} indeterminate={selected.length > 0 && !allChecked}
                                    onChange={toggleAll} sx={{color: t.border, p: "4px", "&.Mui-checked, &.MuiCheckbox-indeterminate": {color: t.green}}}/>
                        </TableCell>
                        <SortableTableCell field="name" sortBy={f.sortBy} sortDirection={f.sortDirection} onSort={handleSort}
                                           sx={{...th, textAlign: "right", width: 200}}>שם</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>תוכנית</TableCell>
                        <SortableTableCell field="lastTrainedDate" sortBy={f.sortBy} sortDirection={f.sortDirection} onSort={handleSort}
                                           sx={{...th, textAlign: "center", width: 90}}>אימון אחרון</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 75, display: {xs: "none", md: "table-cell"}}}>מגמת משקל</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 60, display: {xs: "none", md: "table-cell"}}}>תזונה</TableCell>
                        <SortableTableCell field="lastWeightEntryDate" sortBy={f.sortBy} sortDirection={f.sortDirection} onSort={handleSort}
                                           sx={{...th, textAlign: "center", width: 85, display: {xs: "none", md: "table-cell"}}}>שקילה אחרונה</SortableTableCell>
                        <SortableTableCell field="enabled" sortBy={f.sortBy} sortDirection={f.sortDirection} onSort={handleSort}
                                           sx={{...th, textAlign: "center", width: 75}}>סטטוס</SortableTableCell>
                        <SortableTableCell field="expirationDate" sortBy={f.sortBy} sortDirection={f.sortDirection} onSort={handleSort}
                                           sx={{...th, textAlign: "center", width: 80}}>תוקף מנוי</SortableTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.clients.map(c => {
                        const sel = selected.some(x => x.traineeId === c.traineeId);
                        const lt = relativeTime(c.lastTrainedDate);
                        return (
                            <TableRow key={c.traineeId} onClick={() => navigate(`/app/clients/${c.traineeId}`)}
                                      sx={{
                                        cursor: "pointer",
                                        bgcolor: sel ? `${t.green}1A` : "transparent",
                                        "&:hover td": {background: t.greenLight},
                                        transition: "background .12s ease",
                                      }}>
                              <TableCell sx={{...td, padding: "10px 8px", width: 44}}>
                                <Checkbox size="small" checked={sel} onClick={e => { e.stopPropagation(); toggle(c); }}
                                          sx={{color: t.border, p: "4px", "&.Mui-checked": {color: t.green}}}/>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", overflow: "hidden"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                  <ClientAvatar name={c.name} photoUrl={c.photoUrl} email={c.email}/>
                                  {c.hasPendingApproval && (
                                      <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: t.amber, flexShrink: 0, ml: 0.5}} title="ממתין לאישור"/>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                {c.programName
                                    ? <Box sx={{overflow: "hidden"}}>
                                        <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{c.programName}</Typography>
                                        {programSummary(c) && <Typography sx={{fontSize: "11px", color: t.t3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{programSummary(c)}</Typography>}
                                      </Box>
                                    : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>
                                }
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}}>
                                <Typography sx={{fontSize: "12px", fontWeight: 500, color: lt.color}}>{lt.text}</Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <WeightTrend trend={c.weightTrend} status={c.weightTrendStatus}/>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                {c.hasNutritionMenu
                                    ? <StatusChip label="יש" variant="success" size="small"/>
                                    : <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>}
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                {(() => { const wt = relativeTime(c.lastWeightEntryDate); return <Typography sx={{fontSize: "11px", fontWeight: 500, color: wt.color}}>{wt.text}</Typography>; })()}
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}}>
                                {c.enabled
                                    ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                                    : <StatusChip label="לא פעיל" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", color: expired(c.expirationDate) ? t.redText : expiringSoon(c.expirationDate) ? t.amberText : t.t2, fontWeight: expired(c.expirationDate) || expiringSoon(c.expirationDate) ? 600 : 400}}>
                                {fmt(c.expirationDate)}
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={f.size}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}
                                onItemsPerPageChange={sz => set({size: sz})}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default Clients;
